import {
  Box,
  Typography,
  Button,
  Divider,
  Paper,
  Tooltip,
  Stack,
  Link as MuiLink,
  CircularProgress,
  Alert,
  AlertTitle,
  LinearProgress,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import {
  fetchServiceAccounts,
  selectFirstZapierServiceAccount,
  selectServiceAccountsError,
  selectServiceAccountsLoadingStatus,
} from '../store/service-account/service-accounts.slice'
import { selectZapierAppById } from '../store/zapier/zapier-apps.slice'
import { selectZapierTemplatesByAppId } from '../store/zapier/zapier-templates.slice'
import { IntegrationGroupDetailsPageParams } from './pages'
import { selectIntegrationGroupById } from '../store/integration-group/integration-groups.slice'
import { ZapierCreateZapModal } from './zapier-create-zap-modal'
import Image from 'mui-image'
import { INTEGRATIONS_BASE_URL } from '../../utils/config'
import { error } from 'console'
import { fetchZapierZaps, selectAllZapierZaps, selectZapierZapsLoadingStatus } from '../store/zapier/zapier-zaps.slice'
import { ZapierZapRow } from './zapier-zap'
import { useAppDispatch } from '../store/root'
import { RequestNewIntegrationFormDialog } from './request-new-integration-dialog'
import { useIntegrationsAccess } from '../store/integrations-access'

declare global {
  interface Window {
    loginWindow?: Window | null
  }
}

const ZAPIER_SCOPE = 'zap profile'
const ZAPIER_REDIRECT_URI = `${INTEGRATIONS_BASE_URL}/zapier-oauth`

interface ZapierTabPanelProps {}

export function ZapierTabPanel(_props: ZapierTabPanelProps) {
  const { integrationGroupId, integrationSlug } = useParams<IntegrationGroupDetailsPageParams>()

  const zapierApp = useSelector(selectZapierAppById(`${integrationGroupId}`.replaceAll(/_/g, '-')))
  const zapierAppTemplates = useSelector(selectZapierTemplatesByAppId(`${integrationGroupId}`.replaceAll(/_/g, '-')))
  const zapierServiceAccount = useSelector(selectFirstZapierServiceAccount)
  const isLoggedInToZapier = zapierServiceAccount && 'profile' in zapierServiceAccount.credentials
  const zapierZaps = useSelector(selectAllZapierZaps)
  const zapierZapsLoadingStatus = useSelector(selectZapierZapsLoadingStatus)
  const thisAppsZapierZaps = zapierZaps.filter((zap) =>
    zap.steps.some((step) => step.app?.slug === `${integrationGroupId}`.replaceAll(/_/g, '-'))
  )
  const serviceAccountsLoadingStatus = useSelector(selectServiceAccountsLoadingStatus)
  const serviceAccountsError = useSelector(selectServiceAccountsError)
  // const zapierProfile = zapierServiceAccount?.credentials.profile
  const [zapierModelUrl, setZapierModelUrl] = useState('')

  const [formDialogOpen, setFormDialogOpen] = useState(false)

  const integrationGroup = useSelector(selectIntegrationGroupById(`${integrationGroupId}`))
  const dispatch = useAppDispatch()
  const { canEdit } = useIntegrationsAccess()
  const [pollServiceAccounts, setPollServiceAccounts] = useState(false)

  const [loginWindow, setLoginWindow] = useState<Window | null>(null)

  // Check if a Zapier ServiceAccount was created when we load this component
  useEffect(() => {
    if (serviceAccountsLoadingStatus === 'loaded' && !isLoggedInToZapier) dispatch(fetchServiceAccounts())
  }, [])

  // I don't think we have to fetch the Zaps here since we fetch them whenever the zapierModelUrl is blank
  // // Fetch Zaps if logged into Zapier
  // useEffect(() => {
  //   if (isLoggedInToZapier && zapierServiceAccount) dispatch(fetchZapierZaps({ ownerId: zapierServiceAccount.credentials.profile.id }))
  // }, [isLoggedInToZapier])

  // Poll Service Accounts until there's a Zapier one
  // useEffect(() => {
  //   if (!pollServiceAccounts) return
  //   if (isLoggedInToZapier) {
  //     setPollServiceAccounts(false)
  //     return
  //   }
  //   let intervalId: NodeJS.Timeout
  //   intervalId = setInterval(() => {
  //     console.log('poll...')
  //     dispatch(fetchServiceAccounts())
  //   }, 5000)
  //   return () => clearInterval(intervalId)
  // }, [pollServiceAccounts, dispatch, isLoggedInToZapier])

  // Detect when the user closes loginWindow. It's a cross-origin popup (zapier.com)
  // for most of its lifetime, and cross-origin Window objects only expose a small
  // safelist of members (closed, location, postMessage, opener, parent, top, self,
  // frames, length) - addEventListener/removeEventListener are not among them, so
  // attaching a 'beforeunload' listener directly on it is out-of-spec (silently a
  // no-op in Chrome, but throws a SecurityError in Firefox). Poll `.closed` instead,
  // which is always safe to read cross-origin.
  useEffect(() => {
    if (loginWindow === null) return
    console.log(`Zapier login window opened`)
    window.loginWindow = loginWindow

    const intervalId = setInterval(() => {
      if (loginWindow.closed) {
        console.log(`Zapier login window closing...`)
        setLoginWindow(null)
        dispatch(fetchServiceAccounts())
      }
    }, 500)
    return () => clearInterval(intervalId)
  }, [loginWindow, dispatch])

  // Refresh Zaps when user closes Zapier Modal
  useEffect(() => {
    if (!isLoggedInToZapier || !zapierServiceAccount || zapierModelUrl !== '' || !('profile' in zapierServiceAccount.credentials)) return

    if ('profile' in zapierServiceAccount.credentials) dispatch(fetchZapierZaps({ ownerId: zapierServiceAccount.credentials.profile.id }))
  }, [zapierModelUrl, dispatch, zapierServiceAccount])

  if (integrationGroupId === 'sendhub')
    return (
      <Box sx={{ m: 2, width: '100%' }}>
        <zapier-full-experience
          client-id="98T3iTfdGfdKZD1Ye4lIg79Klv0VMbIf1deGfEED"
          theme="light"
          intro-copy-display="hide"
          app-search-bar-display="show"
          app-exclusions="sms,clicksend,textmagic,salesmsg,simpletexting,ez-texting,textedly"
          app-categories="-phone"
          zap-call-to-action-display="show"
          zap-create-from-scratch-display="show"
        />

        <RequestNewIntegrationFormDialog close={() => setFormDialogOpen(false)} open={formDialogOpen} />
        <Divider />
        <Box sx={{ textAlign: 'center' }}>
          <Button onClick={() => setFormDialogOpen(true)}>Request a Custom Integration</Button>
        </Box>
      </Box>
    )

  return !zapierApp ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        alignItems: 'center',
        paddingTop: 2,
      }}
    >
      <RequestNewIntegrationFormDialog close={() => setFormDialogOpen(false)} open={formDialogOpen} />
      <Typography>
        {integrationGroup
          ? `It looks like Zapier doesn't have a ${integrationGroup.name} integration.`
          : `It looks like Zapier doesn't have an integration for this app.`}
      </Typography>
      <Button onClick={() => setFormDialogOpen(true)}>Request New Integration</Button>
    </Box>
  ) : (
    <>
      <RequestNewIntegrationFormDialog close={() => setFormDialogOpen(false)} open={formDialogOpen} />
      <ZapierCreateZapModal url={zapierModelUrl} close={() => setZapierModelUrl('')} open={!!zapierModelUrl} />
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        {/* Header */}
        <Typography variant="h6">Zapier</Typography>
        <Typography
          sx={{ marginY: 2 }}
          variant="subtitle1"
        >{`Connect SendHub with 1,000+ popular apps and automate your workflows easily with Zapier.`}</Typography>
        <Divider />

        {/* Your Zaps Section */}
        <Typography sx={{ marginY: 2 }} variant="body1">{`Your SendHub ↔ ${zapierApp.title} Zaps`}</Typography>
        {zapierZapsLoadingStatus === 'loading' && <LinearProgress />}
        {!isLoggedInToZapier ? (
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {!!serviceAccountsError && (
                <Alert severity="error">
                  <AlertTitle>There was an error loading your Zapier Account!</AlertTitle>
                  {serviceAccountsError}
                </Alert>
              )}
              {serviceAccountsLoadingStatus === 'loading' ? (
                <CircularProgress />
              ) : canEdit ? (
                <>
                  <Typography>Log in to Zapier to access your Zaps</Typography>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      // Carry our real origin through the OAuth round trip via `state` (not the
                      // redirect_uri itself, which is likely pre-registered with Zapier and must
                      // stay exact) so the popup knows which origin to postMessage back to.
                      const _loginWindow = window.open(
                        `https://zapier.com/oauth/authorize?client_id=98T3iTfdGfdKZD1Ye4lIg79Klv0VMbIf1deGfEED&redirect_uri=${ZAPIER_REDIRECT_URI}&scope=${ZAPIER_SCOPE}&response_type=token&state=${encodeURIComponent(
                          window.location.origin
                        )}`,
                        '_blank',
                        'popup width=599 height=550'
                      )
                      console.log('_loginWindow:', _loginWindow)
                      setLoginWindow(_loginWindow)
                      setPollServiceAccounts(true)
                    }}
                  >
                    Log in
                  </Button>
                </>
              ) : (
                <Typography>Zapier is not connected.</Typography>
              )}
            </Box>
          </Paper>
        ) : thisAppsZapierZaps.length > 0 ? (
          <Box
            sx={{
              maxHeight: 400,
              overflow: 'auto',
              margin: -1,
              padding: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {thisAppsZapierZaps.map((zap, index) => (
              <ZapierZapRow
                key={index}
                zap={zap}
                onClick={() => {
                  if (!canEdit) return
                  setZapierModelUrl(`https://zapier.com/app/editor/${zap.id}`)
                }}
              />
            ))}
          </Box>
        ) : (
          <Typography>No Zaps Found</Typography>
        )}

        <Divider sx={{ marginY: 2 }} />
        <Typography sx={{ marginY: 2 }} variant="subtitle1">{`Popular Zap Templates`}</Typography>
        {zapierAppTemplates.length === 0 && (
          <Typography variant="body2" sx={{ alignSelf: 'center' }}>
            No Zap Templates for <span style={{ fontWeight: 'bold' }}>{`SendHub + ${zapierApp.title}`}</span> exist on Zapier at this time.
          </Typography>
        )}
        {zapierAppTemplates.map((template) => {
          const templateLink =
            `https://zapier.com/webintent/create-zap?template=${template.id}` +
            `&utm_source=partner` +
            `&utm_medium=embed` +
            `&utm_campaign=full_zapier_experience` +
            `&provider=sendhub` +
            `&entry-point-location=partner_embed` +
            // TODO: put the correct referrer urls here...
            `&referer=https%3A%2F%2Fintegrations-dev.sendhub.com%2Fintegrations%2Fzapier%3Futm_source%3Dpartner%26utm_medium%3Dembed%26utm_campaign%3Dfull_zapier_experience%26referer%3Dhttps%253A%252F%252Fintegrations-dev.sendhub.com%252Fintegrations%252Fzapier` +
            `&referrer=https%3A%2F%2Fintegrations-dev.sendhub.com%2Fintegrations%2Fzapier%3Futm_source%3Dpartner%26utm_medium%3Dembed%26utm_campaign%3Dfull_zapier_experience%26referer%3Dhttps%253A%252F%252Fintegrations-dev.sendhub.com%252Fintegrations%252Fzapier`

          return (
            <MuiLink
              underline="none"
              href={templateLink}
              key={templateLink}
              target="_blank"
              onClick={(event) => {
                event.preventDefault()
                if (!canEdit) return
                setZapierModelUrl(templateLink)
              }}
            >
              <Paper
                sx={{
                  p: 2,
                  marginY: 0.5,
                  display: 'flex',
                  flexDirection: {
                    xs: 'column',
                    sm: 'row',
                    md: 'row',
                    // lg: 'none',
                    // xl: 'none',
                  },
                  ':hover': {
                    outlineWidth: 1,
                    outlineStyle: 'solid',
                  },
                }}
              >
                {/* Step Icons */}
                <Tooltip title={template.steps.map((step) => step.title).join(', ')} placement="bottom">
                  <Stack direction="row" spacing={1}>
                    {template.steps.map((step, index) => (
                      <Box
                        key={step.id}
                        sx={{
                          width: '40px',
                          height: '40px',
                          outlineWidth: '1px',
                          outlineColor: 'lightgrey',
                          outlineStyle: 'solid',
                          borderRadius: 'inherit',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Image
                          src={step.image}
                          height="30px"
                          width="30px"
                          fit="scale-down"
                          duration={600}
                          easing="cubic-bezier(0.7, 0, 0.6, 1)"
                          // showLoading={true}
                          errorIcon={true}
                          shift="top"
                          distance="20px"
                          shiftDuration={180}
                          bgColor="inherit"
                        />
                      </Box>
                    ))}
                  </Stack>
                </Tooltip>

                {/* Title */}
                <Stack sx={{ marginX: 1, flexGrow: 1 }} direction="column">
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {template.title}
                  </Typography>
                  <Typography variant="body2">{template.steps.map((step) => step.title).join(' + ')}</Typography>
                </Stack>

                {/* Action Button */}
                <Button
                  variant="outlined"
                  disabled={!canEdit}
                  sx={
                    {
                      /**bgcolor: 'black', color: 'white' */
                    }
                  }
                >
                  Use this Zap
                </Button>
              </Paper>
            </MuiLink>
          )
        })}
        <>
          <Divider sx={{ marginY: 2 }} />
          <Stack direction="column" sx={{ alignItems: 'center' }}>
            <Typography variant="body2">Not finding what you're looking for?</Typography>
            <Stack direction="row">
              {canEdit && <Button onClick={() => setZapierModelUrl('https://zapier.com/webintent/create-zap')}>Create a Zap</Button>}
              {canEdit && (
                <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                  or
                </Typography>
              )}
              <Button onClick={() => setFormDialogOpen(true)}>Request a Custom Integration</Button>
            </Stack>
          </Stack>
        </>
      </Box>
    </>
  )
}
